import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { TabView, TabPanel, TabViewTabChangeParams } from 'primereact/tabview'
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types'
import style from '../ViewComponents/AssetsModal.module.scss'
import { getAssetType } from '../../functions'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import useOnClickOutside from '@aytm/lib/hooks/useOnClickOutside'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

interface IAssetsModalProps {
  schema: IEcommerceAnswerSchema
  assetActiveIndex: number
  tab: number
  onCloseAssetsModel: (currentTab: number) => void
}

const AssetsModel = ({ schema, assetActiveIndex, tab, onCloseAssetsModel }: IAssetsModalProps) => {
  const { t } = useSurveyLocale()
  const timeline = useTimeline()
  const modalBodyRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const playingIndexRef = useRef<number | null>(null)
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(0 === tab ? assetActiveIndex : 0)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(1 === tab ? assetActiveIndex : 0)
  const [activeTabIndex, setActiveTabIndex] = useState<number>(tab)
  const [videoTimecodes, setVideoTimecodes] = useState<{ [key: number]: number }>({})
  const [videoLoaded, setVideoLoaded] = useState<boolean>(false)
  const assetsCollection: assetsCollectionElement[] = useMemo(
    () => [
      ...(schema.image ? [schema.image] : []),
      ...(schema.gallery || []),
      ...(schema.view3d ? [schema.view3d] : [])
    ],
    [schema]
  )
  const videos = useMemo(
    () =>
      [...(schema.gallery || []), ...(schema.view3d ? [schema.view3d] : [])].filter(
        asset => ASSETS_TYPES.VIDEO === getAssetType(asset)
      ),
    [schema]
  )
  const images = useMemo(
    () =>
      [
        ...(schema.image ? [schema.image] : []),
        ...(schema.gallery || []),
        ...(schema.view3d ? [schema.view3d] : [])
      ].filter(asset => ASSETS_TYPES.VIDEO !== getAssetType(asset)),
    [schema]
  )

  const logVideoEvent = (type: 'play_video' | 'pause_video', videoIndex: number) =>
    timeline.log({
      ref: schema.id,
      target: assetsCollection.indexOf(videos[videoIndex]).toString(),
      type
    })

  // The <video> unmounts on tab switch and modal dismissal, so its own pause event never reaches React.
  // Ref-based and idempotent: a pause the browser did deliver clears the ref, so the flush cannot double-log.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  const onTabChange = (event: TabViewTabChangeParams) => {
    if (event.index === activeTabIndex) return // header click on the active tab: nothing closes or opens
    flushPlayingVideo()
    setActiveTabIndex(event.index)
    timeline.log({
      ref: schema.id,
      type: 0 === activeTabIndex ? 'close_video_dialog' : 'close_image_dialog'
    })
    timeline.log({
      ref: schema.id,
      type: 0 === event.index ? 'open_video_dialog' : 'open_image_dialog'
    })
  }
  const onVideoChange = (index: number) => {
    flushPlayingVideo() // src swaps in place — the browser fires abort/emptied, not pause
    timeline.log({
      ref: schema.id,
      type: 'show_asset',
      target: assetsCollection.indexOf(videos[index]).toString(),
      context: ASSETS_TYPES.VIDEO.toLowerCase()
    })
    if (videoRef.current && videoRef.current.currentTime > 2) {
      setVideoTimecodes(prev => {
        return {
          ...prev,
          [selectedVideoIndex]: videoRef.current.currentTime
        }
      })
    }
    setSelectedVideoIndex(index)
    setVideoLoaded(false)
  }
  const onTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime
      const videoDuration = videoRef.current.duration
      if (videoDuration && currentTime > 2 && videoDuration - currentTime > 2 && 0 === Math.floor(currentTime) % 2) {
        setVideoTimecodes(prev => {
          return {
            ...prev,
            [selectedVideoIndex]: currentTime
          }
        })
      }
    }
  }
  const onCanPlay = () => {
    if (videoRef.current && !videoLoaded) {
      const savedTime = videoTimecodes[selectedVideoIndex]
      if (savedTime && savedTime > 2) {
        videoRef.current.currentTime = savedTime
      }
      setVideoLoaded(true)
    }
  }
  const onEnded = () => {
    flushPlayingVideo() // no-op when the browser already delivered the pause before ended
    setVideoTimecodes(prev => {
      const newTimecodes = { ...prev }
      delete newTimecodes[selectedVideoIndex]
      return newTimecodes
    })
    if (selectedVideoIndex + 1 < videos.length) {
      setSelectedVideoIndex(state => state + 1)
    } else if (selectedVideoIndex + 1 >= videos.length) {
      setSelectedVideoIndex(0)
    }
  }
  const onVideoPlay = () => {
    playingIndexRef.current = selectedVideoIndex
    logVideoEvent('play_video', selectedVideoIndex)
  }

  const onVideoPause = () => {
    playingIndexRef.current = null
    logVideoEvent('pause_video', selectedVideoIndex)
  }

  const onImageSelect = (index: number) => {
    setSelectedImageIndex(index)
  }

  useEffect(
    () => () => flushPlayingVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  useEffect(() => {
    if (1 === activeTabIndex) {
      timeline.log({
        ref: schema.id,
        type: 'show_asset',
        target: assetsCollection.indexOf(images[selectedImageIndex]).toString(),
        context: getAssetType(images[selectedImageIndex]).toLowerCase()
      })
    }
    if (0 === activeTabIndex) {
      timeline.log({
        ref: schema.id,
        type: 'show_asset',
        target: assetsCollection.indexOf(videos[selectedVideoIndex]).toString(),
        context: getAssetType(images[selectedVideoIndex]).toLowerCase()
      })
    }
  }, [selectedImageIndex, selectedVideoIndex, schema.id, timeline, assetsCollection, images, videos, activeTabIndex])
  useOnClickOutside(modalBodyRef, () => onCloseAssetsModel(activeTabIndex))

  return (
    <div className={style.modalOverlay}>
      <div
        className={`${style.modalBody} ${
          (videos.length && !images.length) || (!videos.length && images.length) ? style.tabHidden : ''
        }`}
        ref={modalBodyRef}
      >
        <button className={style.close} onClick={() => onCloseAssetsModel(activeTabIndex)}>
          <i className="pi pi-times" />
        </button>
        <TabView onTabChange={onTabChange} activeIndex={activeTabIndex}>
          {videos.length > 0 && (
            <TabPanel header={t('questions', 'ec', 'amazon', 'videos')}>
              <div className={style.assetsWrapper}>
                <div className={style.mainVideo}>
                  <video
                    ref={videoRef}
                    src={(videos[selectedVideoIndex] as unknown as IEcommerceVideoAsset).video.url}
                    preload="metadata"
                    muted
                    controls
                    autoPlay
                    playsInline
                    onTimeUpdate={onTimeUpdate}
                    onCanPlay={onCanPlay}
                    onEnded={onEnded}
                    onPlay={onVideoPlay}
                    onPause={onVideoPause}
                  />
                  <div className={style.share}>
                    <EmptyLink>
                      <i className="pi pi-upload" />
                    </EmptyLink>
                  </div>
                </div>
                <div className={style.videoCollection}>
                  <div className={style.title}>{t('questions', 'ec', 'amazon', 'videosForProduct')}</div>
                  {videos.map((asset, index) => (
                    <div className={style.collectionItem} key={index}>
                      <button
                        onClick={() => onVideoChange(index)}
                        className={`${style.videoPreview} ${index === selectedVideoIndex ? style.active : ''}`}
                        style={{
                          backgroundImage: `url(${(asset as unknown as IEcommerceVideoAsset).video.thumbnail})`
                        }}
                      />
                      <div className={style.videoTitle}>
                        {t('questions', 'ec', 'amazon', 'video')}&nbsp;{index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabPanel>
          )}
          {images.length > 0 && (
            <TabPanel header={t('questions', 'ec', 'amazon', 'images')}>
              <div className={style.assetsWrapper}>
                <div className={style.bigImage}>
                  {ASSETS_TYPES.IMAGE === getAssetType(images[selectedImageIndex]) && (
                    <div
                      className={style.mainImage}
                      role="img"
                      aria-label={
                        (images[selectedImageIndex] as IImageAsset).alt_text || t('questions', 'img', 'noAlt')
                      }
                      style={{
                        backgroundImage: `url(${getBigAssetPath(images[selectedImageIndex] as IImageAsset, 'transparent')})`
                      }}
                    />
                  )}
                  {ASSETS_TYPES.MODEL === getAssetType(images[selectedImageIndex]) && (
                    <div className={style.mainModel}>
                      <model-viewer ar camera-controls src={(images[selectedImageIndex] as IFileAsset).file_url} />
                    </div>
                  )}
                </div>
                <div className={style.imageList}>
                  <div className={style.caption} dangerouslySetInnerHTML={{ __html: schema.text }} />
                  <div className={style.imageCollection}>
                    {images.map((asset, index) => (
                      <button
                        key={index}
                        onClick={() => onImageSelect(index)}
                        className={`${style.imageWrapper} ${index === selectedImageIndex ? style.active : ''}`}
                      >
                        {ASSETS_TYPES.MODEL === getAssetType(asset) && (
                          <model-viewer src={(asset as IFileAsset).file_url} />
                        )}
                        {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                          <div
                            className={style.imagePreview}
                            role="img"
                            aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                            style={{ backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})` }}
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </TabPanel>
          )}
        </TabView>
      </div>
    </div>
  )
}

export default AssetsModel
